import { useState } from "react";
import { Bot, Check, Eye, Lightbulb, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { api, formatApiError } from "../../lib/api";
import { FeedbackRefs } from "./InsightParts";

const OBSERVED = [["dissatisfaction", "Motifs d'insatisfaction"], ["recurring", "Problèmes récurrents"], ["strengths", "Points forts"], ["signals", "Signaux d'attention"]];
const DECISION = { acceptee: "Acceptée", rejetee: "Rejetée", realisee: "Réalisée" };
const PRIO = { haute: "bg-rose-50 text-rose-700", moyenne: "bg-amber-50 text-amber-800", basse: "bg-slate-100 text-slate-600" };

const ObservedBlock = ({ observed }) => (
  <div className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-5" data-testid="ai-observed">
    <p className="mb-1 flex items-center gap-2 font-display font-semibold text-emerald-900"><Eye className="h-4 w-4" />Observé dans les commentaires</p>
    <p className="mb-4 text-xs text-emerald-800">Chaque élément renvoie aux avis réels qui le fondent ; les éléments sans avis source ont été écartés.</p>
    <div className="grid gap-4 md:grid-cols-2">
      {OBSERVED.map(([k, label]) => (
        <div key={k} data-testid={`ai-observed-${k}`}><p className="mb-1 text-sm font-semibold">{label}</p>
          {!observed[k].length ? <p className="text-xs text-slate-500">Rien de saillant.</p> : (
            <ul className="space-y-2 text-sm">{observed[k].map((o, i) => (
              <li key={i}><b>{o.label}</b> <span className="text-xs text-slate-500">({o.evidence} avis)</span><FeedbackRefs ids={o.feedback_ids} />
                {o.explanation && <span className="block text-xs text-slate-600">{o.explanation}</span>}</li>
            ))}</ul>
          )}
        </div>
      ))}
    </div>
  </div>
);

const Recommendation = ({ r, idx, insightId, onChange }) => {
  const [busy, setBusy] = useState(false);
  const decide = async (status) => {
    setBusy(true);
    try { const { data } = await api.put(`/company/insights/${insightId}/recommendations/${idx}`, { status }); onChange(data); toast.success("Décision enregistrée"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <li className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`ai-rec-${idx}`}>
      <div className="flex flex-wrap items-center gap-2"><span className={`rounded-full px-2 py-0.5 text-xs ${PRIO[r.priority]}`}>{r.priority}</span><b className="text-sm">{r.title}</b></div>
      {r.rationale && <p className="mt-1 text-xs text-slate-600">{r.rationale}</p>}
      {r.target && <p className="text-xs text-slate-500">Concerne : {r.target}</p>}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {r.decision ? <span className="text-xs font-medium" data-testid={`ai-rec-${idx}-status`}>{DECISION[r.decision.status]} par {r.decision.by} le {new Date(r.decision.at).toLocaleDateString("fr-FR")}</span>
          : <span className="text-xs text-slate-500" data-testid={`ai-rec-${idx}-status`}>Proposée — en attente de validation humaine</span>}
        {!r.decision && <>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => decide("acceptee")} data-testid={`ai-rec-${idx}-accept`}><Check className="mr-1 h-3.5 w-3.5" />Accepter</Button>
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => decide("rejetee")} data-testid={`ai-rec-${idx}-reject`}><X className="mr-1 h-3.5 w-3.5" />Rejeter</Button></>}
        {r.decision?.status === "acceptee" && <Button size="sm" variant="outline" disabled={busy} onClick={() => decide("realisee")} data-testid={`ai-rec-${idx}-done`}>Marquer réalisée</Button>}
        {r.decision && <Button size="sm" variant="ghost" disabled={busy} onClick={() => decide("proposee")} data-testid={`ai-rec-${idx}-reset`}>Annuler la décision</Button>}
      </div>
    </li>
  );
};

const GeneratedBlock = ({ ins, onChange, readOnly }) => (
  <div className="rounded-2xl border border-amber-200 bg-amber-50/40 p-5" data-testid="ai-generated">
    <p className="mb-1 flex items-center gap-2 font-display font-semibold text-amber-900"><Lightbulb className="h-4 w-4" />Généré par l'IA — hypothèses et recommandations</p>
    <p className="mb-4 text-xs text-amber-800">Aide à la décision : aucune note, aucun commentaire, aucune priorité ni aucun collaborateur n'est modifié automatiquement.</p>
    <p className="mb-4 text-sm leading-relaxed" data-testid="ai-summary-text">{ins.generated.summary}</p>
    {ins.generated.hypotheses.length > 0 && <><p className="mb-1 text-sm font-semibold">Hypothèses à vérifier</p>
      <ul className="mb-4 space-y-1 text-sm" data-testid="ai-hypotheses">{ins.generated.hypotheses.map((h) => <li key={h}>• {h}</li>)}</ul></>}
    <p className="mb-2 text-sm font-semibold">Recommandations</p>
    {!ins.generated.recommendations.length ? <p className="text-xs text-slate-500">Aucune recommandation.</p> : (
      <ul className="space-y-2">{ins.generated.recommendations.map((r, i) => readOnly
        ? <li key={i} className="text-sm">• {r.title}</li>
        : <Recommendation key={i} r={r} idx={i} insightId={ins.id} onChange={onChange} />)}</ul>
    )}
  </div>
);

export const InsightResult = ({ ins, onChange, readOnly = false }) => (
  <div className="space-y-4" data-testid="ai-insight-result">
    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
      <Bot className="h-4 w-4" />{ins.kind === "monthly" ? "Résumé mensuel" : "Analyse"} du {new Date(ins.period.from).toLocaleDateString("fr-FR")} au {new Date(ins.period.to).toLocaleDateString("fr-FR")}
      · {ins.stats.sent} commentaire(s) transmis sur {ins.stats.analyzed} · {ins.stats.masked_items} donnée(s) personnelle(s) masquée(s)
      {ins.stats.excluded_sensitive > 0 && ` · ${ins.stats.excluded_sensitive} dossier(s) confidentiel(s) exclus`} · {ins.model} · confiance IA : {ins.confidence.ai}
    </div>
    <ObservedBlock observed={ins.observed} />
    <GeneratedBlock ins={ins} onChange={onChange} readOnly={readOnly} />
    {ins.limits.length > 0 && <ul className="text-xs text-slate-500" data-testid="ai-limits">{ins.limits.map((l) => <li key={l}>• {l}</li>)}</ul>}
  </div>
);

export const RunButton = ({ status, scope, onDone }) => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post("/company/insights/analyze", scope); onDone(data); toast.success("Analyse IA terminée"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Button onClick={run} disabled={busy || !status?.available} className="rounded-full" data-testid="ai-run-analysis">
      {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Bot className="mr-1.5 h-4 w-4" />}{busy ? "Analyse en cours (jusqu'à 1 min)…" : "Lancer la synthèse IA"}
    </Button>
  );
};
